import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './useAuth';

export interface SubscriptionInfo {
  subscription_status: string | null;
  subscription_plan: string | null;
  trial_end_date: string | null;
  subscription_cancel_at: string | null;
  billing_interval: string | null;
  is_trial: boolean;
  effective_plan: 'free' | 'starter' | 'standard' | 'professional' | 'enterprise';
  can_export: boolean;
  can_sync_on_demand: boolean;
  can_add_orgs: boolean;
  can_use_environments: boolean;
  last_export_date: string | null;
  can_export_today: boolean;
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo>({
    subscription_status: null,
    subscription_plan: null,
    trial_end_date: null,
    subscription_cancel_at: null,
    billing_interval: null,
    is_trial: false,
    effective_plan: 'free',
    can_export: false,
    can_sync_on_demand: false,
    can_add_orgs: false,
    can_use_environments: false,
    last_export_date: null,
    can_export_today: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get account info for the current user
        const { data: account, error } = await supabase
          .from('accounts')
          .select('subscription_status, subscription_tier, subscription_plan, trial_end_date, subscription_cancel_at, billing_cycle, last_export_date')
          .eq('owner_id', user.id)
          .single();

        if (error) {
          console.error('Error fetching subscription:', error);
          setLoading(false);
          return;
        }

        // Check if user is on trial
        const isTrial = account?.subscription_status === 'trialing';

        // Determine effective plan
        // Free/null plans are treated as 'free'
        // Trials get the features of the plan they're trialing
        let effectivePlan: 'free' | 'starter' | 'standard' | 'professional' | 'enterprise' = 'free';

        if (account?.subscription_status === 'active' || isTrial) {
          const tier = account?.subscription_tier?.toLowerCase() || account?.subscription_plan?.toLowerCase();

          if (tier === 'starter') {
            effectivePlan = 'starter';
          } else if (tier === 'standard') {
            effectivePlan = 'standard';
          } else if (tier === 'professional') {
            effectivePlan = 'professional';
          } else if (tier === 'enterprise') {
            effectivePlan = 'enterprise';
          } else if (isTrial) {
            // If trialing but no tier specified, default to standard trial
            effectivePlan = 'standard';
          }
        }

        // Set feature flags based on effective plan
        // Starter: 1 org only, no exports, no environments, no add-ons
        // Standard: 1 environment (5 orgs), no exports, no add-ons
        // Professional: 1 environment (5 orgs), exports, add-ons available
        // Enterprise: Unlimited everything
        const canExport = effectivePlan === 'professional' || effectivePlan === 'enterprise';
        const canSyncOnDemand = effectivePlan === 'professional' || effectivePlan === 'enterprise';
        const canAddOrgs = effectivePlan === 'professional' || effectivePlan === 'enterprise';
        const canUseEnvironments = effectivePlan === 'standard' || effectivePlan === 'professional' || effectivePlan === 'enterprise';

        // Check if user can export today (for trial users with daily limit)
        const today = new Date().toISOString().split('T')[0];
        const lastExportDate = account?.last_export_date;
        const canExportToday =
          effectivePlan === 'professional' ||
          effectivePlan === 'enterprise' ||
          (isTrial && lastExportDate !== today);

        setSubscription({
          subscription_status: account?.subscription_status || null,
          subscription_plan: account?.subscription_plan || account?.subscription_tier || null,
          trial_end_date: account?.trial_end_date || null,
          subscription_cancel_at: account?.subscription_cancel_at || null,
          billing_interval: account?.billing_cycle || null,
          is_trial: isTrial,
          effective_plan: effectivePlan,
          can_export: canExport,
          can_sync_on_demand: canSyncOnDemand,
          can_add_orgs: canAddOrgs,
          can_use_environments: canUseEnvironments,
          last_export_date: lastExportDate || null,
          can_export_today: canExportToday
        });
      } catch (error) {
        console.error('Error in useSubscription:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();

    // Subscribe to account changes
    const channel = supabase
      .channel('account-subscription')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'accounts',
          filter: `owner_id=eq.${user?.id}`
        },
        () => {
          fetchSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return { subscription, loading };
}